/**
 * backtestApi.js
 *
 * API service for the backtest pause control (the admin "Backtest" page).
 * Mirrors killSwitchApi.js: every call logs the request and response status,
 * and errors are rethrown with a consistent { message, detail, status } shape.
 *
 * The backend only sets a request flag; the backtest-driver acts on it and
 * publishes what it did, which comes back as `state`.
 */

import apiClient from './apiClient'

const BASE = '/admin/backtest';

function buildError(context, axiosError) {
  const response = axiosError.response ?? null;
  const status = response ? response.status : 'network-error';
  const body = response ? response.data : axiosError;
  const detail =
    (body && body.detail) ||
    (body && body.message) ||
    (body instanceof Error ? body.message : axiosError.message) ||
    'Unknown error';
  const message = `[backtestApi] ${context} failed (HTTP ${status}): ${detail}`;
  console.error(message, { status, body });
  const err = new Error(message);
  err.detail = detail;
  err.status = status;
  return err;
}

/**
 * Fetch the pause status.
 *
 * @returns {Promise<{ backtest: boolean, requested: boolean, paused: boolean, state: Object|null }>}
 */
export async function getBacktestPauseStatus() {
  console.log(`[backtestApi] GET ${BASE}/pause`);
  try {
    const response = await apiClient.get(`${BASE}/pause`);
    console.log(`[backtestApi] GET ${BASE}/pause → HTTP ${response.status}`);
    return response.data;
  } catch (err) {
    throw buildError('getBacktestPauseStatus', err);
  }
}

/** Ask the driver to pause the replay. Idempotent. */
export async function pauseBacktest() {
  console.log(`[backtestApi] POST ${BASE}/pause`);
  try {
    const response = await apiClient.post(`${BASE}/pause`);
    console.log(`[backtestApi] POST ${BASE}/pause → HTTP ${response.status}`);
    return response.data;
  } catch (err) {
    throw buildError('pauseBacktest', err);
  }
}

/** Clear the pause request so the driver resumes the replay. Idempotent. */
export async function resumeBacktest() {
  console.log(`[backtestApi] DELETE ${BASE}/pause`);
  try {
    const response = await apiClient.delete(`${BASE}/pause`);
    console.log(`[backtestApi] DELETE ${BASE}/pause → HTTP ${response.status}`);
    return response.data;
  } catch (err) {
    throw buildError('resumeBacktest', err);
  }
}
